import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileAudio, Play, Search } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Modal } from "../components/ui/Modal";
import { WaveformPlayer } from "../components/WaveformPlayer";
import { ErrorTile, Td, Th, fmtBytes, fmtDateTime, fmtDuration, fmtTime, todayInTz, tzLabel } from "../components/reports/ReportBits";
import { numbersApi, type NumberListItem } from "../api/numbersApi";
import { recordingAudioUrl, reportsApi, type RecordingItem } from "../api/reportsApi";
import { formatPhoneNumber } from "../lib/phone";
import { HttpError } from "../api/http";
import { useSession } from "../hooks/useSession";
import { ScrollShadow } from "../components/ui/ScrollShadow";

const filterInput: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontFamily: "inherit",
  fontSize: "0.9rem",
  background: "white",
};

const firstOfMonth = () => `${todayInTz().slice(0, 7)}-01`;

/**
 * Recordings report: every call recording of the customer in a date range
 * (optionally for one number), newest first. The play button opens a modal
 * with the call and recording details and a waveform player; audio is
 * streamed through the API after an ownership check.
 */
export function RecordingsReportPage() {
  const { session } = useSession();
  // "RecordingsSearchOnly" logins get no list or filters: only the search by call id.
  const searchOnly = session?.restriction === "RecordingsSearchOnly";
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || firstOfMonth();
  const to = params.get("to") || todayInTz();
  const dnisId = params.get("dnisId") ? Number(params.get("dnisId")) : null;

  const [numbers, setNumbers] = useState<NumberListItem[]>([]);
  const [items, setItems] = useState<RecordingItem[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<RecordingItem | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notFound, setNotFound] = useState<string | null>(null);

  useEffect(() => {
    if (searchOnly) return;
    let active = true;
    numbersApi.list().then((r) => active && setNumbers(r.items ?? [])).catch(() => undefined);
    return () => {
      active = false;
    };
  }, [searchOnly]);

  useEffect(() => {
    if (searchOnly) return;
    let active = true;
    setLoading(true);
    setError(null);
    reportsApi
      .recordings(from, to, dnisId)
      .then((r) => {
        if (!active) return;
        setItems(r.items);
        setTruncated(r.truncated);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load recordings"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [from, to, dnisId, searchOnly]);

  const setFilter = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const numberLabel = useMemo(() => {
    const m = new Map<number, string>();
    for (const n of numbers) m.set(n.dnisId, formatPhoneNumber(n.number, n.countryCode, n.areaCode) || n.number);
    return m;
  }, [numbers]);

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <FileAudio size={22} aria-hidden="true" /> Recordings
          </span>
        }
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => setSearchOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Search size={16} /> Search
          </button>
        }
      />
      <div className="dashboard">
        {searchOnly && (
          <section className="tile" style={{ display: "grid", gap: 10, justifyItems: "start" }}>
            <div style={{ fontWeight: 700 }}>Search a recording</div>
            <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
              Enter the call id of a call to play or download its recording.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => setSearchOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Search size={16} /> Search by call id
            </button>
          </section>
        )}
        {!searchOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", fontSize: "0.9rem", margin: "4px 0 12px" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--portal-muted)" }}>From</span>
            <input type="date" className="portal-input-inline" style={filterInput} value={from} max={to} onChange={(e) => setFilter({ from: e.target.value })} />
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--portal-muted)" }}>To</span>
            <input type="date" className="portal-input-inline" style={filterInput} value={to} min={from} onChange={(e) => setFilter({ to: e.target.value })} />
          </label>
          <label style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--portal-muted)" }}>Number</span>
            <select className="portal-input-inline" value={dnisId ?? ""} onChange={(e) => setFilter({ dnisId: e.target.value })} style={{ ...filterInput, minWidth: 180 }}>
              <option value="">All numbers</option>
              {numbers.map((n) => (
                <option key={n.dnisId} value={n.dnisId}>{numberLabel.get(n.dnisId)}</option>
              ))}
            </select>
          </label>
        </div>

        )}
        {!searchOnly && error && <ErrorTile message={error} />}
        {!searchOnly && !error && (
          <section className="tile" style={{ padding: 0 }}>
            <ScrollShadow><table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9rem" }}>
              <thead>
                <tr>
                  <Th>Date / time ({tzLabel()})</Th>
                  <Th>Number</Th>
                  <Th>Calling number</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th align="right">Length</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {items == null && loading && (
                  <tr><td colSpan={7} style={{ padding: 16, color: "var(--portal-muted)", textAlign: "center" }}>Loading recordings…</td></tr>
                )}
                {items != null && items.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 16, color: "var(--portal-muted)", textAlign: "center" }}>No recordings in this period.</td></tr>
                )}
                {items?.map((r) => (
                  <tr key={r.callId} style={loading ? { opacity: 0.6 } : undefined}>
                    <Td>{fmtDateTime(r.inviteUtc)}</Td>
                    <Td>{numberLabel.get(r.dnisId) ?? r.dnisPresentation ?? "—"}</Td>
                    <Td>{r.callingNumber || "—"}</Td>
                    <Td>{fmtTime(r.acceptUtc)}</Td>
                    <Td>{fmtTime(r.disconnectUtc)}</Td>
                    <Td align="right">
                      <span title={r.lengthIsEstimate ? "Estimated from the file size; exact after playing" : "Measured from the recording"}>
                        {r.recordingSeconds != null ? `${r.lengthIsEstimate ? "~" : ""}${fmtDuration(r.recordingSeconds)}` : "—"}
                      </span>
                    </Td>
                    <Td align="right">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setSelected(r)}
                        aria-label={`Play recording of ${fmtDateTime(r.inviteUtc)}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px" }}
                      >
                        <Play size={14} /> Play
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table></ScrollShadow>
            {truncated && (
              <div style={{ padding: "10px 14px", color: "var(--portal-muted)", fontSize: "0.85rem", borderTop: "1px solid var(--portal-border)" }}>
                Only the newest 500 recordings are shown; narrow the period or pick a number to see the rest.
              </div>
            )}
          </section>
        )}
      </div>

      <SearchRecordingModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onFound={(item) => {
          setSearchOpen(false);
          setSelected(item);
        }}
        onNotFound={(callId) => {
          setSearchOpen(false);
          setNotFound(callId);
        }}
      />
      <Modal
        open={notFound != null}
        onClose={() => setNotFound(null)}
        title="Recording not found"
        footer={
          <button type="button" className="btn btn-primary" onClick={() => setNotFound(null)}>
            Close
          </button>
        }
      >
        <p style={{ margin: 0 }}>
          No recording was found for call id <code>{notFound}</code> among your numbers.
        </p>
      </Modal>
      <RecordingModal
        recording={selected}
        numberLabel={selected ? numberLabel.get(selected.dnisId) : undefined}
        onClose={() => setSelected(null)}
        onUpdated={(fresh) => {
          // The exact length is known once the file has been played: reflect it in the list too.
          setSelected((cur) => (cur && cur.callId === fresh.callId ? fresh : cur));
          setItems((cur) => cur?.map((i) => (i.callId === fresh.callId ? fresh : i)) ?? cur);
        }}
      />
    </>
  );
}

/** Looks up one recording by call id (server-side, within the customer's numbers only). */
function SearchRecordingModal({ open, onClose, onFound, onNotFound }: {
  open: boolean;
  onClose: () => void;
  onFound: (item: RecordingItem) => void;
  onNotFound: (callId: string) => void;
}) {
  const [callId, setCallId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCallId("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const trimmed = callId.trim();
  const search = async () => {
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      onFound(await reportsApi.recording(trimmed));
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) onNotFound(trimmed);
      else setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Search recording"
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void search()} disabled={!trimmed || busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Search size={16} /> {busy ? "Searching…" : "Search"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
        style={{ display: "grid", gap: 8 }}
      >
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Call Id</span>
          <input
            className="portal-input-inline"
            style={{ ...filterInput, width: "100%", padding: "8px 10px", fontFamily: "inherit" }}
            value={callId}
            onChange={(e) => setCallId(e.target.value)}
            placeholder="e.g. 00000000-0000-0000-0000-000000000000"
            autoFocus
            spellCheck={false}
          />
        </label>
        {error && <ErrorTile message={error} />}
      </form>
    </Modal>
  );
}

/**
 * Call + recording details with a waveform player and a download button.
 * The audio is fetched once as a blob: the player and the download share
 * it, and after the fetch the details are reloaded because the API has
 * just measured the exact length from the file.
 */
function RecordingModal({ recording: r, numberLabel, onClose, onUpdated }: {
  recording: RecordingItem | null;
  numberLabel?: string;
  onClose: () => void;
  onUpdated: (fresh: RecordingItem) => void;
}) {
  const [audio, setAudio] = useState<{ callId: string; url: string; blob: Blob } | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    if (!r) return;
    let active = true;
    setAudio(null);
    setAudioError(null);
    (async () => {
      try {
        const res = await fetch(recordingAudioUrl(r.callId), { credentials: "include" });
        if (!res.ok) throw new Error(res.status === 404 ? "The recording file is not available." : `Could not load the recording (${res.status})`);
        const blob = await res.blob();
        if (!active) return;
        setAudio({ callId: r.callId, url: URL.createObjectURL(blob), blob });
        // The server stored the measured length while serving the file.
        reportsApi.recording(r.callId).then((fresh) => active && onUpdated(fresh)).catch(() => undefined);
      } catch (err) {
        if (active) setAudioError(err instanceof Error ? err.message : "Could not load the recording");
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.callId]);

  // Release the object URL when the modal closes or another recording is opened.
  useEffect(() => () => {
    if (audio) URL.revokeObjectURL(audio.url);
  }, [audio]);

  const download = () => {
    if (!r || !audio) return;
    const a = document.createElement("a");
    a.href = audio.url;
    a.download = `REC_${r.callId}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal
      open={r != null}
      onClose={onClose}
      title={r ? `Recording for ${r.callId}` : "Recording"}
      maxWidth={720}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={download} disabled={!audio} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Download size={16} /> Download
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {r && (
        <div style={{ display: "grid", gap: 16 }}>
          {audio && audio.callId === r.callId ? (
            <WaveformPlayer key={r.callId} src={audio.url} height={64} ariaLabel="Play recording" />
          ) : audioError ? (
            <ErrorTile message={audioError} />
          ) : (
            <div style={{ padding: 20, textAlign: "center", color: "var(--portal-muted)", fontSize: "0.9rem" }}>Loading recording…</div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
            <Details
              title="Call"
              rows={[
                ["Start", fmtDateTime(r.acceptUtc)],
                ["End", fmtDateTime(r.disconnectUtc)],
                ["Number", numberLabel ?? r.dnisPresentation ?? "—"],
                ["Calling number", r.callingNumber || "—"],
                ["Direction", r.direction === "OUT" ? "Outbound" : "Inbound"],
                ["Length", r.callSeconds != null ? fmtDuration(r.callSeconds) : "—"],
              ]}
            />
            <Details
              title="Recording"
              rows={[
                ["Recorded", fmtDateTime(r.createdUtc)],
                ["Length", r.recordingSeconds != null ? `${r.lengthIsEstimate ? "~" : ""}${fmtDuration(r.recordingSeconds)}${r.lengthIsEstimate ? " (estimate)" : ""}` : "—"],
                ["File size", fmtBytes(r.fileSize)],
              ]}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

function Details({ title, rows }: { title: string; rows: [string, React.ReactNode][] }) {
  return (
    <div style={{ display: "grid", gap: 6, alignContent: "start" }}>
      <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{title}</div>
      <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 4, fontSize: "0.85rem" }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: "contents" }}>
            <dt style={{ color: "var(--portal-muted)", whiteSpace: "nowrap" }}>{k}</dt>
            <dd style={{ margin: 0, overflowWrap: "anywhere" }}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
